import { useEffect, useRef, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import _get from 'lodash/get';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import ButtonPrint from '../../../atoms/button-print';
import ButtonViewGlossary from '../../../atoms/button-view-glossary';
import DropdownHeaderDocuments from '../../../atoms/dropdown-header-documents';
import DropdownHeaderResources from '../../../atoms/dropdown-header-resources';
import DropdownShareClass from '../../../atoms/dropdown-share-class';
import ErrorBox from '../../../atoms/error-box';
import Icon from '../../../atoms/icon/index';
import ModalBasic from '../../../atoms/modal-basic';
import ModalInvest from './modals/modal-invest';
import ModalDocuments from './modals/modal-documents';
import SubNavBar from '../../../atoms/sub-nav-bar';
import styles from './page-header.module.scss';
import Tag from '../../../molecules/screener/tag';
import usePageHeaderStore from '../../../../stores/profile/modules/use-page-header';
import useProfileCommonStore from '../../../../stores/profile/use-profile-common-store';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import configuredComponent from '../../../atoms/button-disclosures';
import { TEMPLATES } from '../../../../stores/profile/get-profile-template';
import { FUND_TYPES } from '../../../../stores/profile/get-fund-type';
import DOMPurify from 'dompurify';

const className = augmentClassName(styles);

const PageHeader = ({
  children,
  displayAsFundType, // Please make sure provided values are supported within the analytics switch bellow.
  fundName = '',
  hideForPWAChannelMobile,
  sectionNavOptions,
  showDisclosuresButton
}) => {
  const {
    documents,
    dropdownShareClasses,
    fundNotification,
    isExternalSite,
    selectedItem,
    isInvestModalVisible,
    handleClickInvestButton,
    handleCloseInvestModal
  } = usePageHeaderStore();

  const { commonState = {} } = useProfileCommonStore();
  const { auxiliary = {}, pageOffsetsTop = {}, channel = '' } = commonState;

  const xref = {};

  const fundNameOrType = _get(
    commonState,
    'xref.data.name',
    _get(commonState, 'xref.data.fundType', 'this fund')
  );
  const isMicrosites = channel === 'microsites';
  const isIFA = channel === 'ifa';
  const isMyTiaa = channel === 'mytiaa';
  const paddingClass = isMicrosites ? 'pt-8' : 'pt-6';

  const getSanitizedText = (originString) => {
    const cleanHtmlString = DOMPurify.sanitize(originString);
    return (
      <span dangerouslySetInnerHTML={{ __html: cleanHtmlString }} />
    )
  }

  if (auxiliary.isLoaded) {
    xref.fundName = _get(auxiliary, 'data.xref.name');
    xref.fundType = _get(auxiliary, 'data.fundType');
    xref.symbol = _get(auxiliary, 'data.xref.symbol');
  }
  //TSV fund names don't come from xref, using the fund name from the quote call instead.
  if (fundName) {
    xref.fundName = fundName;
  }

  const stickyHeaderRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalValues, setModalValues] = useState([]);
  const [isLeavingSiteModalVisible, setIsLeavingSiteModalVisible] = useState(false);
  const [isFundNotificationModalVisible, setIsFundNotificationModalVisible] = useState(false);

  const handleShow = e => setIsFundNotificationModalVisible(true);

  const handleClose = e => setIsFundNotificationModalVisible(false);
  const showSymbol =
    [
      TEMPLATES.afterTaxAnnuity,
      TEMPLATES.afterTaxAnnuityMoneyMarket,
      TEMPLATES.fdicInsuredBankProduct,
      TEMPLATES.fixedAnnuityTiaaTraditional,
      TEMPLATES.fixedAnnuityTiaaStableValue,
      TEMPLATES.fixedAnnuityOther,
      TEMPLATES.other,
      TEMPLATES.otherMoneyMarket,
      TEMPLATES.retirePlus,
      TEMPLATES.variableAnnuityTiaaAccess,
      TEMPLATES.variableAnnuityTiaaAccessMoneyMarket,
      TEMPLATES.variableAnnuityOther,
      TEMPLATES.variableAnnuityTiaaAccessNonProp
    ].indexOf(`Template: ${_get(auxiliary, 'data.profileTemplate')}`) === -1;

  const isFundATA = commonState.fundType === FUND_TYPES.afterTaxAnnuity;
  const isFundQdia = commonState.fundType === FUND_TYPES.fixedAnnuityTiaaQdia;

  useEffect(() => {
    if (
      !stickyHeaderRef ||
      !stickyHeaderRef.current ||
      (isMicrosites && pageOffsetsTop === 0) ||
      isIFA
    ) {
      return;
    } else if (pageOffsetsTop === 0 && isMyTiaa) {
      window.dispatchEvent(new Event('resize'));
      return;
    }
    const tiaaHeaderHeight = pageOffsetsTop;

    stickyHeaderRef.current.style.top = `${tiaaHeaderHeight}px`;
  }, [isIFA, isMicrosites, isMyTiaa, pageOffsetsTop]);

  const dropdownShareClass =
    !isFundATA && !isFundQdia && dropdownShareClasses && dropdownShareClasses.length ? (
      <DropdownShareClass items={dropdownShareClasses} selectedItem={selectedItem} />
    ) : null;

  const openModalWithValues = values => {
    setModalValues(values);
    setIsModalVisible(true);
  };

  const openLeavingSiteModal = function () {
    setIsLeavingSiteModalVisible(true);
  };

  const leavingSiteClick = function () {
    window.open(
      documents.find(doc => doc.label.includes('Fact Sheet')).url,
      '_blank',
      'noopener,noreferrer'
    );
    setIsLeavingSiteModalVisible(false);
  };

  const analytics = {};
  switch (displayAsFundType) {
    case 'Collective Investment Trust':
      analytics.Id = 'H_CollectiveInvestmentTrust';
      break;
    case 'FDIC Insured Bank Product':
      analytics.Id = 'H_FdicInsuredBankProduct';
      break;
    case 'Fixed Annuity':
      analytics.Id = 'H_FixedAnnuity';
      break;
    case 'Mutual Fund':
      analytics.Id = 'H_MutualFund';
      break;
    case 'Other Investments':
      analytics.Id = 'H_OtherInvestment';
      break;
    case 'RetirePlus Model Portfolio':
      analytics.Id = 'H_RetirePlus';
      break;
    case 'TIAA Access Variable Annuity':
      analytics.Id = 'H_TiaaAccess';
      break;
    case 'Variable Annuity':
      analytics.Id = 'H_VariableAnnuity';
      break;
    case 'Stable Value':
      analytics.Id = 'H_StableValue';
      break;
    default:
      break;
  }

  return (
    <>
      <header
        data-analytics-id={analytics.Id}
        data-analytics-section="subpage"
        ref={stickyHeaderRef}
        {...className(
          'bg-primary',
          'font-family-agenda',
          'text-white',
          'sticky-header',
          isMicrosites && 'microsites-print-sticky-header',
          paddingClass
        )}
      >
        <ModalDocuments.MultipleDocuments
          modalValues={modalValues}
          onHide={() => setIsModalVisible(false)}
          show={isModalVisible}
        />

        <ModalDocuments.LeavingSite
          externalSiteClick={leavingSiteClick}
          onHide={() => setIsLeavingSiteModalVisible(false)}
          show={isLeavingSiteModalVisible}
        />

        <Container
          data-analytics-componenttype="hero-component"
          data-analytics-id={analytics.Id}
          data-analytics-section="component"
          fluid={'xl'}
        >
          <Row>
            <Col>
              <div
                {...className(
                  'header__super-header',
                  'text-uppercase',
                  'text-light',
                  'header-fund-type-symbol',
                  'h5',
                  'font-weight-normal',
                  'font-family-roboto'
                )}
              >
                <span {...className('d-inline-block')}>{displayAsFundType}</span>
                {showSymbol ? (
                  <span
                    {...className('d-inline-block', 'border-left', 'border-light', 'ml-3', 'pl-3')}
                  >
                    {xref.symbol}
                  </span>
                ) : undefined}
              </div>
              {auxiliary.isLoaded && !xref.fundName ? (
                <ErrorBox
                  errorMessage={
                    'There is no data available for the symbol you entered. Please check that you entered it correctly.'
                  }
                />
              ) : (
                <h1 {...className('header__fund-name')}>{xref.fundName}
                  {fundNotification && <Tag popoverMsg={"Click here for fund notifications."} index={0} isProfilePage onSvgClick={handleShow}><Icon.Info style={{ fontSize: '10px' }} /></Tag>}</h1>
              )}
            </Col>
            <Col
              {...className('align-items-start', 'd-flex', 'text-right', 'text-white')}
              xs="auto"
            >
              <div
                {...className(
                  'header__button-container',
                  'align-items-center',
                  'd-inline-flex',
                  'pr-2',
                  'pr-sm-3',
                  'pr-xl-0',
                  'd-print-none'
                )}
              >
                <span {...className('d-none', 'd-sm-inline', 'mr-5')}>{dropdownShareClass}</span>
                {hideForPWAChannelMobile ? undefined : (
                  <span {...className('d-lg-none', 'mr-3')}>
                    {/* Mobile and tablet kebab menu */}
                    <DropdownHeaderResources
                      items={documents}
                      isExternalSite={isExternalSite}
                      triggerExternalSiteModal={openLeavingSiteModal}
                      triggerModal={openModalWithValues}
                    />
                  </span>
                )}

                <ButtonPrint
                  {...className('text-white', 'd-none', 'd-lg-inline-flex', 'flex-column')}
                />
                <div {...className('header-print-buttons-separator')}></div>
                <ButtonPrint
                  {...className('text-white', 'd-none', 'd-lg-inline-flex', 'flex-column')}
                  icon="SavePDF"
                />

                <span {...className('d-none', 'd-lg-inline')}>
                  <DropdownHeaderDocuments
                    items={documents}
                    isExternalSite={isExternalSite}
                    triggerExternalSiteModal={openLeavingSiteModal}
                    triggerModal={openModalWithValues}
                  />
                </span>
                <ButtonViewGlossary
                  {...className('text-white', 'd-none', 'd-lg-inline', 'header__glossary')}
                  iconStyle={{ marginBottom: '2px' }}
                />
              </div>
            </Col>
          </Row>
          <div {...className('d-sm-none', 'mb-4')}>{dropdownShareClass}</div>
          <div {...className('mt-sm-3', 'mt-lg-5')}>
            {children}
            <div {...className('text-right', 'mt-3', 'header-disclosures-btn')}>
              <ButtonDisclosures color={'text-white'} module={'page-header'} />
            </div>
          </div>
        </Container>
        {hideForPWAChannelMobile ? undefined : (
          <div
            data-analytics-componenttype="jump-link-component"
            data-analytics-id="JL_FundNavigation"
            data-analytics-section="component"
            {...className('bg-white', 'nav-shadow', 'no-print')}
          >
            <Container
              as="nav"
              fluid={'xl'}
              {...className('nav', 'd-flex', 'align-items-center', 'justify-content-between')}
            >
              <div {...className('w-100', 'h-100')}>
                {sectionNavOptions ? (
                  <SubNavBar
                    hideInvestButton={hideForPWAChannelMobile}
                    navOptions={sectionNavOptions}
                    onClickInvestButton={handleClickInvestButton}
                  />
                ) : null}
              </div>
              <div>
                {showDisclosuresButton ? (
                  <configuredComponent.Button
                    classes={[
                      'font-size-12',
                      'font-weight-bold',
                      'text-uppercase',
                      'font-family-arial-mt'
                    ]}
                    module="Overall Investment"
                    variant={'action'}
                  />
                ) : null}
              </div>
            </Container>
          </div>
        )}
      </header>
      <ModalInvest
        show={isInvestModalVisible}
        onHide={handleCloseInvestModal}
        fundNameOrType={fundNameOrType}
      />
      <ModalBasic show={isFundNotificationModalVisible} onHide={handleClose}>
        <ModalBasic.Header>
          <ModalBasic.Title>
            {'Fund Notification'}
            <ModalBasic.Subtitle>{xref.fundName}</ModalBasic.Subtitle>
          </ModalBasic.Title>
          <ModalBasic.CloseButton onClick={handleClose} />
        </ModalBasic.Header>
        <ModalBasic.Body>
          {getSanitizedText(fundNotification)}
        </ModalBasic.Body>
      </ModalBasic>
    </>
  );
};

PageHeader.defaultProps = {
  showDisclosuresButton: false,
  fundName: ''
};

PageHeader.propTypes = {
  children: PropTypes.node,
  displayAsFundType: PropTypes.string,
  fundName: PropTypes.string,
  hideForPWAChannelMobile: PropTypes.bool,
  sectionNavOptions: PropTypes.array,
  showDisclosuresButton: PropTypes.bool
};

export default PageHeader;
